<template>
  <div class="search-results">
    <div ref="refSearchList" class="search-list">
      <div v-if="searchResultListsIsEmpty && !isSearching" class="search-no-result">
        <div ref="refSearchBg" class="search-no-result-img"></div>
        <div class="search-no-result-content">{{ $t('owork_base.no_search_results') }}</div>
      </div>
      <div v-if="!searchResultListsIsEmpty" class="search-result-list">
        <div
          v-for="(data, index) in searchResultLists"
          :key="data.provider.id"
          class="search-result-list-item"
          :class="{ isLastSearchItem: index === searchResultLists.length - 1 }"
        >
          <SearchResultGroup :data="data" @onclick-search-item="onclickSearchItem" @open="open" />
        </div>
        <a-spin :spinning="isSearching" :indicator="indicator" class="search-loading-spin" :class="{ isSearching }">
          <template #tip>
            <span class="spin-tip">{{ isStandardScreen ? $t('owork_base.searching') : $t('owork_base.loading') }}</span>
          </template>
        </a-spin>
      </div>
      <a-spin
        v-if="searchResultListsIsEmpty && isSearching"
        :spinning="isSearching"
        :indicator="indicator"
        class="search-loading-spin"
      >
        <template #tip>
          <span class="spin-tip">{{ isStandardScreen ? $t('owork_base.searching') : $t('owork_base.loading') }}</span>
        </template>
      </a-spin>
    </div>
    <div v-show="currentClickSearchListId" class="search-result-detail">
      <SearchResultItemDetailComponent
        ref="refSearchResultItemDetail"
        @open="open"
        @open-in-folder="openInFolder"
        @cancel="closeDetails"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
  import type { Ref } from 'vue'
  import { computed, ref, onMounted, nextTick, h } from 'vue'
  import SearchResultItemDetailComponent from './SearchResultItemDetailComponent.vue'
  import SearchResultGroup from './SearchResultGroup.vue'
  import { INSTANCE as SearchManager } from '@core/search/SearchManager'
  import { handleSearchListsItem } from './SearchResultsHandle'
  import type { SearchResultListItem, SearchResponseData } from '@core/search/SearchProvider'
  import { PageManager } from '@/core/application/PageManager'
  import { MicroAppId } from '@core/application/ApplicationConstants'
  import { debounce, isEmpty, throttle } from 'lodash'
  import { encodeRelativePathWithParams } from '@/core/Utils'
  import type { AnimationItem } from 'lottie-web'
  import lottie from 'lottie-web'
  import bgNoSearchResult from '@assets/animation/bg-no-search-result.json'
  import animationSearching from '@assets/animation/animation-searching.json'
  import { TimeConstants } from '@core/Constants'
  import { onClickOutside, useScroll } from '@vueuse/core'
  import { useSharedScreen } from '@/core/compositions/Screen'
  import { LoadingOutlined } from '@ant-design/icons-vue'

  const { isStandardScreen } = useSharedScreen()

  const emit = defineEmits(['open-search-item-folder'])

  const isSearching = ref<boolean>(true)
  const indicator = h(LoadingOutlined, {
    style: {
      fontSize: '14px',
      color: 'rgba(0, 0, 0, 0.2)',
    },
    spin: true,
  })
  let searchResultLists: Ref<SearchResponseData[]> = ref([])

  const searchResultListsIsEmpty = computed(() =>
    isEmpty(searchResultLists.value.filter((item) => item.list.length !== 0))
  )

  const getSearchData = () => {
    const searchResponseLists = SearchManager.getSearchList()

    for (let item of searchResponseLists) {
      item = handleSearchListsItem(item)
    }

    searchResultLists.value = searchResponseLists

    handleDataAfterGet()
  }

  const updateSearchDataById = (id: string) => {
    const provider = SearchManager.getSearchResponseDataById(id)

    if (!provider) return

    const index = searchResultLists.value.findIndex((item) => item.provider.id === id)

    if (index === -1) return

    searchResultLists.value[index] = handleSearchListsItem(provider[0])

    handleDataAfterGet()
  }

  const handleDataAfterGet = () => {
    if (searchResultListsIsEmpty.value) {
      nextTick(() => {
        loadBackgroundAnimation()
        loadAnimation.value?.goToAndPlay(0)
      })
    }
  }

  const currentClickSearchListId = ref<string>()

  const checkSearchItemIsAvailable = () => {
    const item = searchResultLists.value.find((item) => item.provider.id === currentClickSearchListId.value)

    if (!item || item.provider.isConnected !== true) {
      currentClickSearchListId.value = undefined
      changeSearchResultItemDetail(undefined)
    }
  }

  const initClickSearchItem = () => {
    const searchResult = searchResultLists.value.find((findItem) => findItem.list.length !== 0)
    const item = searchResult?.list[0]

    if (item) {
      item.active = true
      currentClickSearchListId.value = searchResult?.provider.id
    }

    changeSearchResultItemDetail(item)
  }

  const onclickSearchItem = (item: SearchResultListItem, id: string) => {
    for (const searchList of searchResultLists.value) {
      for (const searchListItem of searchList.list) {
        searchListItem.active = false
      }
    }

    item.active = true
    currentClickSearchListId.value = id

    changeSearchResultItemDetail(item)
  }

  const pageManager = new PageManager(MicroAppId.FILE_MANAGER)

  const open = (item: SearchResultListItem) => {
    if (!item || !currentClickSearchListId.value) return

    const path = encodeRelativePathWithParams('', {
      type: item.type,
      id: currentClickSearchListId.value,
      filename: item.filename,
      path: item.absolutePath,
    })

    pageManager.openInNewWindow(path, { title: item.filename })
  }

  const FILE_TYPE_ALL = 'all'

  const openInFolder = (item: SearchResultListItem) => {
    if (!item) return

    const targetPath = item.absolutePath

    const path = encodeRelativePathWithParams(FILE_TYPE_ALL, {
      deviceId: currentClickSearchListId.value,
      targetPath: targetPath.substring(0, targetPath.lastIndexOf('\/')),
    })

    pageManager.goto(path)

    emit('open-search-item-folder')
  }

  const refSearchResultItemDetail = ref<InstanceType<typeof SearchResultItemDetailComponent> | null>(null)

  const changeSearchResultItemDetail = (item: SearchResultListItem | undefined) => {
    refSearchResultItemDetail.value?.searchItem(item)
  }

  const refSearchList = ref()

  const debounceFetchSearchList = debounce(
    async () => {
      await SearchManager.fetchSearchList()
    },
    1000,
    {
      leading: true,
      trailing: false,
    }
  )

  // only display once when search no result
  let loadAnimation = ref<AnimationItem | null>(null)
  let searchLoadingAnimation = ref<AnimationItem | null>(null)

  const refSearchBg = ref()

  const refSearchLoading = ref()

  const loadBackgroundAnimation = () => {
    loadAnimation.value?.destroy()
    loadAnimation.value = lottie.loadAnimation({
      container: refSearchBg.value,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: bgNoSearchResult,
    })
  }

  const loadSearchLoadingAnimation = () => {
    if (refSearchLoading.value) {
      searchLoadingAnimation.value = lottie.loadAnimation({
        container: refSearchLoading.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: animationSearching,
      })
    }
  }

  const resetSearch = () => {
    closeDetails()
    searchResultLists.value = []
  }

  const closeDetails = () => {
    changeSearchResultItemDetail(undefined)
    currentClickSearchListId.value = undefined
  }

  onMounted(() => {
    const { arrivedState } = useScroll(refSearchList, {
      onStop() {
        if (isSearching.value) return

        if (arrivedState.bottom) {
          debounceFetchSearchList()
        }
      },
    })

    SearchManager.addChangeListener({
      onSearchListClear: () => {
        resetSearch()
      },
      onSearchListUpdated: () => {
        getSearchData()
      },
      onSearchListInit: () => {
        isStandardScreen.value && initClickSearchItem()
      },
      onSearchListItemConnectUpdated: (id: string) => {
        updateSearchDataById(id)
        checkSearchItemIsAvailable()
      },
      onSearchStateUpdated(searchState) {
        isSearching.value = searchState
        nextTick(() => {
          if (refSearchList.value && arrivedState.bottom) {
            refSearchList.value.scrollTop = refSearchList.value.scrollHeight
          }
        })
      },
    })

    onClickOutside(refSearchResultItemDetail.value?.$el, closeDetails)
  })

  defineExpose({ getSearchData, resetSearch })
</script>
<style scoped lang="stylus">

  ::-webkit-scrollbar
    width 4px
  ::-webkit-scrollbar-track
    background #fafafa
  ::-webkit-scrollbar-thumb
    background #e5e5e5
    border-radius 4px
  .search-results
    border 1px solid #ebecf0
    box-shadow 0px 20px 36px rgba(16, 22, 82, 0.06)
    border-radius 24px
    overflow hidden
    rect(648px, 480px)
    margin-top 9px
    touch-action pan-y
    display flex
    background #ffffff
    .search-list
      rect(321px, 480px)
      background-color #fafafa
      border-right 1px solid rgba(0, 0, 0, 0.06)
      overflow-y auto
      .search-no-result
        flex-align-center()
        flex-direction column
        height 100%
        .search-no-result-img
          margin-top 112px
          rect(280px, 200px)
        .search-no-result-content
          font-size 16px
          line-height 21px
          color rgba(0, 0, 0, 0.3)
      .search-result-list
        padding 16px 16px 0
        & img
          rect(20px)
        .search-list-loading
          text-align center
    .search-result-detail
      width 326px
      background-color #ffffff
  .v-enter-from
  .v-leave-to
    opacity 0
  .v-enter-active
  .v-leave-active
    transition opacity 0.5s ease

  :deep(.search-loading-spin)
    height 32px !important
    flex-direction row !important
    position static !important
    &.isSearching
      height 64px !important
    .spin-tip
      font-size 12px
      color lighter_shallow_color
      margin-left 4px
      font-weight default_font_weight
    .ant-spin-text
      margin-top 0 !important
  .search-result-list-item
    margin-bottom 16px
  .isLastSearchItem
    margin-bottom 0

  @import '../styles/h5Adaptive/SearchInputComponent.styl'
</style>
